/* ==========================================================================
   as-klaviyo-signup.js
   <as-klaviyo-signup> — three steps, one container, TWO independent
   subscribe calls to Klaviyo's client-side Subscribe API.

   THE LOAD-BEARING RULE
   The email subscription fires on STEP 1 SUBMIT, before step 2 is shown.
   Nothing is held in memory to be submitted at the end. If it were, every
   person who abandons at the SMS step would be lost entirely. If the SMS
   call then fails, the email subscription is untouched — never rolled back,
   never re-submitted.

   Steps swap in place. No modal, no navigation, no scroll jump. The
   container is measured on load and pinned to the height of its tallest
   step, so the footer cannot jump as the steps change.

   Endpoint    POST https://a.klaviyo.com/client/subscriptions/?company_id=…
   Auth        PUBLIC api key only, in the query string. This endpoint is
               designed for the browser. A private key must never appear
               in client-side code.
   Success     HTTP 202 with an empty body. Not parsed as JSON.
               An existing profile also returns 202 — that is a normal
               success, not an "already subscribed" error.

   Only the channel being consented to is ever sent: call 1 carries email
   consent, call 2 carries SMS consent. Call 2 includes the email address
   as the identifier so the SMS consent lands on the SAME profile rather
   than creating a duplicate.

   A THIRD CALL, to /client/profiles/, follows a successful call 2 and
   records the reporting properties. Two hard rules govern it, both
   learned by breaking this in production:

     1. THE PROPERTIES CANNOT RIDE ALONG ON A SUBSCRIBE CALL. Those
        endpoints take email, phone_number and subscriptions and nothing
        else. Add a properties object and the whole request 400s, taking
        the SMS consent down with it. Hence a separate call.
     2. NEVER PUT $source IN THAT PROPERTIES OBJECT. It is reserved. The
        API does not set it, it rejects the write and discards the entire
        properties object with it — so gave_phone and sms_optin_source
        vanish too and you end up worse off than sending nothing.

   The third call is non-blocking for the same reason as rule 1: consent
   must never depend on a reporting write succeeding.

   ON $source. Step 1's custom_source is what populates it. Verified on a
   real signup: an existing profile keeps that value through all three
   calls. A profile created fresh by these calls has been seen to end up
   with Klaviyo's own "added via API" code, which reads as -9, so do not
   assume $source is populated for everyone — segment on the properties
   below, which are written by us and are stable in both cases.

     completed step 2  ->  gave_phone is true
     email only        ->  gave_phone is not set

   DOUBLE OPT-IN OUTRANKS ANYTHING SENT HERE. A list set to double opt-in
   holds consent at NEVER_SUBSCRIBED until the confirmation is answered,
   no matter what this file posts — the profile joins the list, and the
   channel stays unconsented. That is a list setting, not a bug in this
   code. Klaviyo applies it to email but not to SMS, so the visible symptom
   is one channel subscribing and the other not.
   ========================================================================== */

(function () {
  'use strict';

  var ENDPOINT = 'https://a.klaviyo.com/client/subscriptions/';

  /* Custom properties cannot ride along on a subscribe call — the subscribe
     endpoints accept email, phone_number and subscriptions and nothing else,
     and reject the whole request with a 400 if you add to that. Properties
     need their own call, to their own endpoint. */
  var PROFILE_ENDPOINT = 'https://a.klaviyo.com/client/profiles/';

  /* ------------------------------------------------------------ meta pixel

     ONE FIRE PER EVENT PER PAGE VIEW, NOT PER FORM. The guest list carries
     three instances of this element — hero, sticky bar and close — all on the
     same page and all reaching the same list. Someone who submits in the hero
     and again in the footer is one lead, not two. Counted twice, cost-per-lead
     reads about 30% better than reality and nothing in Meta flags it.

     This lives at module scope so the guard is shared by every instance on the
     page rather than held per element, which would not guard anything.

     The event ID is kept for the server-side Conversions API call. Meta
     deduplicates browser against server on event_name + event_id, so the
     server event must carry this exact value or one person is counted twice.

     No fbq on the page means no call and no error. The pixel is installed
     outside the theme and may not be present on every environment. */
  var PIXEL_FIRED = {};

  function newEventId() {
    if (window.crypto && typeof window.crypto.randomUUID === 'function') {
      return window.crypto.randomUUID();
    }
    return String(Date.now()) + '-' + Math.random().toString(16).slice(2);
  }

  function firePixel(name, contentName, contentCategory) {
    if (!name || PIXEL_FIRED[name]) return;
    if (typeof window.fbq !== 'function') {
      console.warn('[as-klaviyo-signup] fbq not on the page, so ' + name + ' was not sent.');
      return;
    }

    PIXEL_FIRED[name] = true;

    var eventId = newEventId();
    var params = {};
    if (contentName) params.content_name = contentName;
    if (contentCategory) params.content_category = contentCategory;

    window.fbq('track', name, params, { eventID: eventId });

    // Where the server call reads it from. __bohoLeadEventId is the name the
    // tracking spec uses; the map carries the rest.
    window.__bohoPixelEventIds = window.__bohoPixelEventIds || {};
    window.__bohoPixelEventIds[name] = eventId;
    if (name === 'Lead') window.__bohoLeadEventId = eventId;
  }

  /* Dev notes: "Preserve and pass through all UTM parameters into the Klaviyo
     profile. Without it the ad spend teaches us nothing." Read once, at load,
     because the two-step flow never navigates and a later read would still
     see the same URL. */
  var UTM_KEYS = ['utm_source', 'utm_medium', 'utm_campaign', 'utm_content', 'utm_term'];

  function readUtms() {
    var found = {};
    if (!window.URLSearchParams) return found;
    var params = new URLSearchParams(window.location.search);
    UTM_KEYS.forEach(function (key) {
      var value = params.get(key);
      // Only what is actually present. Writing empty strings would overwrite a
      // returning subscriber's original attribution with nothing.
      if (value) found[key] = value;
    });
    return found;
  }

  class AsKlaviyoSignup extends HTMLElement {
    connectedCallback() {
      this.publicKey = this.dataset.publicKey || '';
      this.listId = this.dataset.listId || '';
      this.smsListId = this.dataset.smsListId || this.listId;
      this.revision = this.dataset.revision || '2026-07-15';
      this.dialCode = this.dataset.dialCode || '+1';
      this.source1 = this.dataset.source1 || 'Website — step 1 email';
      this.source2 = this.dataset.source2 || 'Website — step 2 SMS';
      this.sendingLabel = this.dataset.sendingLabel || 'Sending';

      this.steps = Array.prototype.slice.call(this.querySelectorAll('[data-step]'));
      if (!this.steps.length) return;

      this.email = '';
      this.current = 0;
      this.busy = false;

      this._onResize = this._onResize.bind(this);

      this._bind();
      this._go(0, true);

      /* Pin the height once the real fonts are in, or the measurement is
         taken against the fallback face and comes out short. */
      this._measure();
      if (document.fonts && document.fonts.ready) {
        document.fonts.ready.then(this._measure.bind(this));
      }
      window.addEventListener('resize', this._onResize, { passive: true });
    }

    disconnectedCallback() {
      window.removeEventListener('resize', this._onResize);
    }

    /* ------------------------------------------------------------- wiring */
    _bind() {
      var self = this;

      this.querySelectorAll('[data-signup-submit]').forEach(function (btn) {
        btn.addEventListener('click', function (event) {
          event.preventDefault();
          var step = parseInt(btn.dataset.signupSubmit, 10);
          if (step === 1) self._submitEmail(btn);
          else self._submitSms(btn);
        });
      });

      /* Enter should submit the step she is on, without a native form post. */
      this.querySelectorAll('[data-signup-input]').forEach(function (input) {
        input.addEventListener('keydown', function (event) {
          if (event.key !== 'Enter') return;
          event.preventDefault();
          var step = input.closest('[data-step]');
          var btn = step && step.querySelector('[data-signup-submit]');
          if (btn) btn.click();
        });
      });

      /* "Just email is fine" — no call, straight to the confirmation. */
      var skip = this.querySelector('[data-signup-skip]');
      if (skip) {
        skip.addEventListener('click', function (event) {
          event.preventDefault();
          self._go(2);
        });
      }
    }

    /* ------------------------------------------------------------- layout */
    _onResize() {
      if (this._raf) cancelAnimationFrame(this._raf);
      this._raf = requestAnimationFrame(this._measure.bind(this));
    }

    /* Every step is in the DOM and laid out; only visibility differs, so any
       step can be measured without being shown.

       The container follows the ACTIVE step's height and animates between
       them, rather than being pinned to the tallest. Pinning to the tallest
       also prevents a jump, but step 2 carries the consent block and is far
       taller than step 1 — so it reserves a large empty gap under the form
       that never gets used. An animated resize solves the jump without the
       dead space. */
    _measure() {
      var step = this.steps[this.current];
      if (!step) return;
      var h = step.offsetHeight;
      if (h > 0) this.style.setProperty('--as-signup-height', h + 'px');
    }

    _go(index, silent) {
      this.current = index;

      this.steps.forEach(function (step, i) {
        var on = i === index;
        step.dataset.active = on ? 'true' : 'false';
        step.setAttribute('aria-hidden', on ? 'false' : 'true');
        /* Keep hidden steps out of the tab order without display:none,
           which would break the height measurement. */
        step.querySelectorAll('input, button, a').forEach(function (el) {
          if (on) el.removeAttribute('tabindex');
          else el.setAttribute('tabindex', '-1');
        });
      });

      this._measure();

      if (!silent) {
        var step = this.steps[index];
        if (step) step.focus({ preventScroll: true });
      }

      /* Announced so anything outside the element can follow the state —
         the guest list sticky bar hides itself once step 1 is done. Fired
         on the element, bubbling, rather than exposing `current`, so the
         listener never reaches inside. */
      this.dispatchEvent(
        new CustomEvent('as-signup:step', {
          bubbles: true,
          detail: { step: index + 1, total: this.steps.length }
        })
      );
    }

    /* --------------------------------------------------------- public api */

    /* Submit an email captured somewhere else on the page — the sticky
       opt-in bar. The address is written into the step 1 field first, so
       the same validation, the same error slot and the same custom_source
       all apply: it is the same signup reached from a different place, and
       must not become a second source value.

       Returns false when the address is invalid, so the caller can show its
       own error and stay put rather than advancing. */
    submitEmail(value) {
      var input = this.steps[0] && this.steps[0].querySelector('[data-signup-input]');
      if (!input) return false;
      if (!this._validEmail(String(value || '').trim())) return false;

      input.value = String(value).trim();
      var btn = this.steps[0].querySelector('[data-signup-submit]');
      this._submitEmail(btn);
      return true;
    }

    /* Lets an outside caller check a value before committing to it. */
    isValidEmail(value) {
      return this._validEmail(String(value || '').trim());
    }

    /* ------------------------------------------------------------- states */
    _error(step, message) {
      var el = this.steps[step] && this.steps[step].querySelector('[data-signup-error]');
      if (!el) return;
      el.textContent = message || '';
      el.hidden = !message;
    }

    _busy(btn, on) {
      this.busy = on;
      if (!btn) return;
      var label = btn.querySelector('[data-signup-label]') || btn;
      if (on) {
        btn.dataset.restLabel = label.textContent;
        label.textContent = this.sendingLabel;
        btn.setAttribute('disabled', '');
        btn.setAttribute('aria-busy', 'true');
      } else {
        if (btn.dataset.restLabel) label.textContent = btn.dataset.restLabel;
        btn.removeAttribute('disabled');
        btn.removeAttribute('aria-busy');
      }
    }

    /* --------------------------------------------------------- validation */
    _validEmail(value) {
      return /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(value.trim());
    }

    /* The selected country's dial code, falling back to the element's
       default when no selector is rendered. */
    _dialCode() {
      var sel = this.querySelector('[data-signup-dial]');
      return (sel && sel.value) || this.dialCode;
    }

    /* E.164 — dial code followed by the national number, digits only.
       She types the national number; the prefix comes from the selector.
       If she pastes a full international number anyway, the prefix is
       stripped rather than doubled. */
    _toE164(value) {
      var dial = this._dialCode();
      var dialDigits = dial.replace(/\D/g, '');
      var digits = String(value).replace(/\D/g, '');

      if (dial === '+1') {
        /* NANP: exactly ten digits. No US area code starts with 1, so a
           leading 1 on an 11-digit entry is always the country code. */
        if (digits.length === 11 && digits.charAt(0) === '1') digits = digits.slice(1);
        if (digits.length !== 10) return null;

        /*
          TEN DIGITS IS NOT ENOUGH ON ITS OWN.

          Plenty of national numbers are ten digits once you include their
          trunk zero — a Romanian mobile is 07xx xxx xxx. Length alone
          accepts it, glues +1 on the front, and posts +10712345678 to
          Klaviyo. Klaviyo answers 202, quietly discards the number as
          unvalidatable, and records no SMS consent. The form shows the
          thank-you step, the profile shows no phone, and nothing anywhere
          reports an error. That failure cost real debugging time.

          NANP forbids 0 and 1 as the leading digit of both the area code
          and the exchange, which is exactly what catches a foreign trunk
          zero. Rejecting here means she is told to check the number while
          she can still fix it, instead of being thanked for nothing.
        */
        if (!/^[2-9]\d{2}[2-9]\d{6}$/.test(digits)) return null;

        return dial + digits;
      }

      /* Elsewhere national lengths vary too much to check precisely, so
         only strip a pasted prefix when what remains is still plausible. */
      if (digits.indexOf(dialDigits) === 0 && digits.length - dialDigits.length >= 6) {
        digits = digits.slice(dialDigits.length);
      }
      /* A leading trunk zero is national notation and is dropped in E.164. */
      if (digits.length > 6 && digits.charAt(0) === '0') digits = digits.slice(1);

      if (digits.length < 6 || digits.length > 14) return null;
      return dial + digits;
    }

    /* ------------------------------------------------------------- the API */

    /* A list id is optional to Klaviyo — without one it falls back to the
       account's default opt-in settings. An EMPTY id is not the same thing
       and would 400, so drop the relationship entirely rather than send a
       blank, and say so in the console. */
    _relationships(listId) {
      if (listId) return { list: { data: { type: 'list', id: listId } } };
      console.warn(
        '[as-klaviyo-signup] No list ID set on this form. Subscribers will go to ' +
          "Klaviyo's account default rather than a specific list. Set the List ID in the theme editor."
      );
      return undefined;
    }

    _post(body, url) {
      if (!this.publicKey) {
        console.error(
          '[as-klaviyo-signup] No Klaviyo public API key set. Nothing will be sent. ' +
            'Add it in the theme editor: Klaviyo → Settings → API keys → public key / site ID.'
        );
        return Promise.resolve({ status: 0, ok: false });
      }

      return fetch((url || ENDPOINT) + '?company_id=' + encodeURIComponent(this.publicKey), {
        method: 'POST',
        headers: {
          'content-type': 'application/json',
          revision: this.revision
        },
        body: JSON.stringify(body)
      });
    }

    /*
      The step-2 answer, written somewhere it can be segmented on.

      THIS PAYLOAD IS EXACTLY THE ONE VERIFIED WORKING ON A LIVE SIGNUP —
      two properties, both ours, neither reserved. Confirmed on a real
      profile carrying $source, gave_phone and sms_optin_source together.
      Do not add to it without testing: $source was tried here and the API
      rejected the write and discarded the other two along with it.

      gave_phone is only ever written true, and only here. Writing false on
      step 1 would read more tidily, but it would erase a previous opt-in
      for a returning subscriber and silently drop them out of the segment.

      FIRE AND FORGET. This runs after consent is already recorded and must
      never affect it. Failing here costs a reporting property; blocking the
      confirmation on it would cost a subscriber. Logged, never surfaced.
    */
    /*
      Attribution, written after consent is already recorded so it can never
      affect it. Fire and forget, exactly like _tagPhoneOptIn: failing here
      costs a reporting property, blocking on it would cost a subscriber.

      Sent as plain utm_* properties. $source is reserved and rejected — and a
      rejected write discards every other property with it.
    */
    _tagUtms() {
      var utms = readUtms();
      if (!Object.keys(utms).length) return;

      this._post(
        {
          data: {
            type: 'profile',
            attributes: {
              email: this.email,
              properties: utms
            }
          }
        },
        PROFILE_ENDPOINT
      )
        .then(function (res) {
          if (res.status === 202 || res.ok) return;
          console.warn('[as-klaviyo-signup] UTMs not saved', res.status);
        })
        .catch(function (err) {
          console.warn('[as-klaviyo-signup] UTMs not saved', err);
        });
    }

    _tagPhoneOptIn() {
      this._post(
        {
          data: {
            type: 'profile',
            attributes: {
              /* Email only as the identifier. The phone number is already
                 being written by the subscribe call; sending it here as well
                 would race that write for no gain. */
              email: this.email,
              properties: {
                gave_phone: true,
                sms_optin_source: this.source2
              }
            }
          }
        },
        PROFILE_ENDPOINT
      )
        .then(function (res) {
          if (res.status === 202 || res.ok) return;
          console.warn('[as-klaviyo-signup] opt-in properties not saved', res.status);
        })
        .catch(function (err) {
          console.warn('[as-klaviyo-signup] opt-in properties not saved', err);
        });
    }

    _submitEmail(btn) {
      if (this.busy) return;

      var input = this.steps[0].querySelector('[data-signup-input]');
      var value = input ? input.value.trim() : '';

      if (!this._validEmail(value)) {
        this._error(0, input.dataset.invalidMessage || 'Enter a valid email address.');
        input.focus();
        return;
      }

      this._error(0, '');
      this._busy(btn, true);
      this.email = value;

      var self = this;
      this._post({
        data: {
          type: 'subscription',
          attributes: {
            custom_source: this.source1,
            profile: {
              data: {
                type: 'profile',
                attributes: {
                  email: value,
                  subscriptions: { email: { marketing: { consent: 'SUBSCRIBED' } } }
                }
              }
            }
          },
          relationships: this._relationships(this.listId)
        }
      })
        .then(function (res) {
          self._busy(btn, false);
          if (res.status === 202 || res.ok) {
            self._tagUtms();
            // On successful submission of step one only — never on page load,
            // and never again from the other forms on this page.
            firePixel(
              self.dataset.pixelEvent,
              self.dataset.pixelContentName,
              self.dataset.pixelContentCategory
            );
            self._go(1);
          } else {
            console.error('[as-klaviyo-signup] email subscribe failed', res.status, res);
            self._error(0, self.dataset.failMessage || 'Something went wrong. Try again.');
          }
        })
        .catch(function (err) {
          self._busy(btn, false);
          console.error('[as-klaviyo-signup] email subscribe error', err);
          self._error(0, self.dataset.failMessage || 'Something went wrong. Try again.');
        });
    }

    _submitSms(btn) {
      if (this.busy) return;

      var input = this.steps[1].querySelector('[data-signup-input]');
      var phone = this._toE164(input ? input.value : '');

      if (!phone) {
        this._error(1, input.dataset.invalidMessage || 'Enter a valid 10-digit phone number.');
        input.focus();
        return;
      }

      this._error(1, '');
      this._busy(btn, true);

      var self = this;

      /*
        NO custom_source ON THIS CALL — DELIBERATE.

        custom_source is written to the profile as $source. Step 2 runs
        second, so sending one here stamps every person who gave a phone
        number with "step 2", overwriting the acquisition source recorded
        by step 1 and destroying the only signal that separates the people
        who stopped at email from the people who went on. Confirmed on a
        live profile: $source read back exactly the step 1 string until
        step 2 landed.

        The SMS channel keeps its own consent record either way — method
        stays "Text message API" on the profile. Where the opt-in came
        from is recorded as a profile property by _tagPhoneOptIn below,
        which is a place step 1 never writes to.
      */
      this._post({
        data: {
          type: 'subscription',
          attributes: {
            profile: {
              data: {
                type: 'profile',
                attributes: {
                  /* The identifier that merges this consent onto the profile
                     created by call 1. Without it you get two profiles. */
                  email: this.email,
                  phone_number: phone,
                  subscriptions: { sms: { marketing: { consent: 'SUBSCRIBED' } } }
                }
              }
            }
          },
          relationships: this._relationships(this.smsListId)
        }
      })
        .then(function (res) {
          self._busy(btn, false);
          if (res.status === 202 || res.ok) {
            /*
              202 DOES NOT MEAN THE NUMBER WAS ACCEPTED.

              Klaviyo takes the request, keeps the email, and throws away a
              phone number it cannot validate — still answering 202. Treating
              that as success is how someone reaches the thank-you step with
              no SMS consent recorded anywhere. The only signal is a header.

              It is only readable if Klaviyo lists it in
              Access-Control-Expose-Headers; cross-origin it may come back
              null even when set. Null therefore means "cannot tell", not
              "fine", so we carry on — a header we are not allowed to read
              must never block a real subscriber.
            */
            var partial = null;
            try {
              partial = res.headers.get('X-Klaviyo-Partial-Failure');
            } catch (e) {
              partial = null;
            }

            if (partial && partial.indexOf('phone_number') !== -1) {
              console.error(
                '[as-klaviyo-signup] Klaviyo accepted the request but discarded the phone ' +
                  'number (' + phone + '). Usual causes: the number is not real, or the ' +
                  'account has no sending number covering that country. No SMS consent was ' +
                  'recorded. Klaviyo partial-failure header: ' + partial
              );
              self._error(1, input.dataset.invalidMessage || 'Enter a valid 10-digit phone number.');
              input.focus();
              return;
            }

            /* Consent is recorded. The reporting properties are a separate,
               non-blocking call — see _tagPhoneOptIn. */
            self._tagPhoneOptIn();
            /* Its own event, never a second Lead. Reached only past the
               partial-failure check above, so it means real SMS consent
               rather than a 202 that dropped the number. */
            firePixel(
              self.dataset.pixelEventSms,
              self.dataset.pixelContentName,
              self.dataset.pixelContentCategory
            );
            self._go(2);
          } else {
            /* Her email subscription from step 1 still stands. Never roll
               it back, never re-submit it. */
            console.error('[as-klaviyo-signup] sms subscribe failed', res.status, res);
            self._error(1, self.dataset.failMessage || 'Something went wrong. Try again.');
          }
        })
        .catch(function (err) {
          self._busy(btn, false);
          console.error('[as-klaviyo-signup] sms subscribe error', err);
          self._error(1, self.dataset.failMessage || 'Something went wrong. Try again.');
        });
    }
  }

  if (!customElements.get('as-klaviyo-signup')) {
    customElements.define('as-klaviyo-signup', AsKlaviyoSignup);
  }
})();
